import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Heatmap } from '../components/Heatmap';
import { HabitFormModal } from '../components/HabitFormModal';
import { HabitReminderDialog } from '../components/HabitReminderDialog';
import { ShareDialog } from '../components/ShareDialog';
import { ChevronLeft, Flame, Pencil, Archive as ArchiveIcon, Trash2, Share2, Bell } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { calculateStreaks, completionRate } from '../lib/streaks';
import { rescheduleHabitReminders } from '../lib/localNotifications';
import { toast } from 'sonner';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

export default function HabitDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [habit, setHabit] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [category, setCategory] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: h, error: hE }, { data: l }] = await Promise.all([
      supabase.from('habits').select('*').eq('id', id).maybeSingle(),
      supabase.from('habit_logs').select('*').eq('habit_id', id).order('log_date'),
    ]);
    if (hE) toast.error(hE.message);
    setHabit(h);
    setLogs(l || []);
    setLoading(false);
    if (h?.category_id) {
      const { data: c } = await supabase.from('categories').select('*').eq('id', h.category_id).maybeSingle();
      setCategory(c);
    } else {
      setCategory(null);
    }
  }, [user, id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (form) => {
    const { data, error } = await supabase.from('habits').update(form).eq('id', id).select().maybeSingle();
    if (error) { toast.error(error.message); return; }
    setHabit(data);
    toast.success('Habit updated');
    rescheduleHabitReminders();
  };

  const toggleArchive = async () => {
    const { data, error } = await supabase.from('habits').update({ archived_at: habit.archived_at ? null : new Date().toISOString() }).eq('id', id).select().maybeSingle();
    if (error) { toast.error(error.message); return; }
    setHabit(data);
    toast.success(data.archived_at ? 'Archived' : 'Restored');
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${habit.name}"? This removes all its history.`)) return;
    const { error } = await supabase.from('habits').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Deleted');
    navigate('/habits');
  };

  if (loading) return <Skeleton className="h-96 w-full rounded-2xl" data-testid="habit-detail-loading" />;
  if (!habit) return (
    <div className="text-center py-20" data-testid="habit-not-found">
      <p className="text-muted-foreground">Habit not found</p>
      <Link to="/habits" className="text-primary hover:underline mt-3 inline-block">Back to habits</Link>
    </div>
  );

  const Icon = LucideIcons[toIconKey(habit.icon)] || LucideIcons.Sparkles;
  const streaks = calculateStreaks(habit, logs);
  const rate30 = completionRate(habit, logs, 30);
  const rate90 = completionRate(habit, logs, 90);

  const recentNotes = logs.filter((l) => l.note || l.mood).slice(-5).reverse();

  return (
    <div className="space-y-8" data-testid="habit-detail-page">
      <Link to="/habits" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="habit-detail-back">
        <ChevronLeft className="h-4 w-4 mr-1" /> All habits
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${habit.color}1F`, color: habit.color }}>
            <Icon className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="habit-detail-name">{habit.name}</h1>
            {habit.description && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{habit.description}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {category && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full"
                  style={{ backgroundColor: `${category.color}22`, color: category.color }}
                  data-testid="habit-detail-category"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                  {category.name}
                </span>
              )}
              <p className="text-xs text-muted-foreground capitalize">{habit.type} · {habit.schedule_type.replace('_', ' ')} · {habit.time_of_day}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="ghost"
            className={`rounded-full ${habit.reminder_time ? 'text-primary' : ''}`}
            onClick={() => setReminderOpen(true)}
            data-testid="habit-detail-reminder"
          >
            <Bell className="h-4 w-4" />
            {habit.reminder_time ? `Reminder · ${habit.reminder_time}` : 'Set reminder'}
          </Button>
          <Button variant="ghost" className="rounded-full" onClick={() => setShareOpen(true)} data-testid="habit-detail-share"><Share2 className="h-4 w-4" />Share</Button>
          <Button variant="ghost" className="rounded-full" onClick={() => setEditOpen(true)} data-testid="habit-detail-edit"><Pencil className="h-4 w-4" />Edit</Button>
          <Button variant="ghost" className="rounded-full" onClick={toggleArchive} data-testid="habit-detail-archive"><ArchiveIcon className="h-4 w-4" />{habit.archived_at ? 'Restore' : 'Archive'}</Button>
          <Button variant="ghost" className="rounded-full text-destructive hover:text-destructive" onClick={remove} data-testid="habit-detail-delete"><Trash2 className="h-4 w-4" />Delete</Button>
        </div>
      </header>

      {/* stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Current streak', value: streaks.current, icon: Flame, tone: 'text-secondary' },
          { label: 'Longest streak', value: streaks.longest, icon: Flame, tone: 'text-primary' },
          { label: 'Last 30 days', value: `${rate30}%`, icon: null, tone: '' },
          { label: 'Last 90 days', value: `${rate90}%`, icon: null, tone: '' },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card/30 p-5" data-testid={`stat-card-${i}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{s.label}</div>
            <div className="mt-2 flex items-center gap-2">
              {s.icon && <s.icon className={`h-5 w-5 ${s.tone}`} />}
              <div className="font-heading text-2xl font-semibold">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* heatmap */}
      <section className="rounded-2xl border border-border bg-card/30 p-5 sm:p-7">
        <h2 className="font-heading font-medium mb-4">Activity, last 12 months</h2>
        <Heatmap habit={habit} logs={logs} />
      </section>

      {/* recent notes */}
      <section>
        <h2 className="font-heading font-medium mb-3">Recent reflections</h2>
        {recentNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reflections yet. Tap a habit&apos;s menu on Today to add one.</p>
        ) : (
          <div className="space-y-3">
            {recentNotes.map((l) => (
              <div key={l.id} className="rounded-2xl border border-border bg-card/30 p-4" data-testid={`recent-note-${l.id}`}>
                <div className="flex justify-between items-start">
                  <span className="text-xs text-muted-foreground">{l.log_date}</span>
                  {l.mood && <span className="text-xl">{l.mood}</span>}
                </div>
                {l.note && <p className="text-sm mt-1">{l.note}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      <HabitFormModal open={editOpen} onOpenChange={setEditOpen} initial={habit} onSubmit={submit} />
      <HabitReminderDialog
        habit={habit}
        open={reminderOpen}
        onOpenChange={(o) => { setReminderOpen(o); if (!o) refresh(); }}
      />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} habit={habit} userId={user.id} />
    </div>
  );
}
